import React, { useCallback, useEffect } from 'react'

import { Controller, useForm } from 'react-hook-form'

import { Button, Spinner, TextInput } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, string } from 'prop-types'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useTracker from 'Pods/Shared/Hooks/useTracker'
import I18n from 'Pods/Shared/I18n'
import FacebookIcon from 'Pods/Shared/Icons/FacebookIcon'
import GoogleIcon from 'Pods/Shared/Icons/GoogleIcon'

import MTCaptcha from 'Utils/MTCaptcha'
import { NOOP } from 'Utils/stableDefaults'

const EMAIL_PATTERN = /^[^@]+@[^.]+\..+$/
const CAPTCHA_ID = 'registration-captcha'
const DEFAULT_GUEST_CHECKOUT_PATH = '/guests/sessions'

function EmailStep({
  locale = 'en',
  guestCheckoutPath = DEFAULT_GUEST_CHECKOUT_PATH,
  onSubmit = NOOP,
  onLoginClick = NOOP,
}) {
  const { postData, isLoading: isPosting } = useApiPost()
  const { getData: checkEmailAvailability, isLoading: isChecking } = useApiGet()
  const isSubmitting = isPosting || isChecking
  const { trackEvent } = useTracker()

  const captchaEnabled = window.gon?.features?.guest_captcha_enabled

  useEffect(() => {
    trackEvent(SubscriptionEvents.GUEST_EMAIL_STEP_VIEWED)
  }, [])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: { email: '', email_confirmation: '' },
  })

  const t = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  function createGuestSession(email, honeypot) {
    postData({
      path: guestCheckoutPath,
      data: {
        email,
        email_confirmation: honeypot,
        ...(captchaEnabled && {
          MTCaptchaToken: window.mtcaptcha?.getVerifiedToken(CAPTCHA_ID),
        }),
      },
      onSuccess: ({ data }) => {
        if (typeof data === 'string') {
          // MTCaptcha challenge HTML returned by the server; replace the document
          // to render it, mirroring the legacy Shared/UserV2/GuestCheckout flow.
          document.write(data)
          return
        }

        onSubmit(email, true)
      },
      onError: (error) => {
        if (captchaEnabled) {
          window.mtcaptcha?.resetUI(CAPTCHA_ID)
        }
        const message = error.response?.data?.message
          || error.response?.data?.errors
          || t('subscription_checkout.payment_error_toast')
        App.events.publish('app:notification:flash:error', message)
      },
    })
  }

  function onFormSubmit({ email, email_confirmation: honeypot }) {
    trackEvent(SubscriptionEvents.GUEST_EMAIL_SUBMITTED)

    checkEmailAvailability({
      path: '/api/internal/users/email/availability',
      params: { email },
      onSuccess: ({ data }) => {
        if (data.success) {
          createGuestSession(email, honeypot)
        } else {
          onSubmit(email, false)
        }
      },
      onError: () => {
        App.events.publish(
          'app:notification:flash:error',
          t('subscription_checkout.payment_error_toast'),
        )
      },
    })
  }

  function handleSocialLogin(e) {
    e.preventDefault()
    e.target.submit()
  }

  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const originParam = encodeURIComponent(window.location.href)

  return (
    <div className="email-step">
      <h1 className="email-step__title cgt-heading-300">
        {t('subscription_checkout.email_step.title')}
      </h1>
      <p className="email-step__subtitle cgt-body-base">
        {t('subscription_checkout.email_step.subtitle')}
      </p>

      <div className="email-step__social-buttons">
        <form
          className="email-step__social-form"
          action={`/users/auth/google_oauth2?origin=${originParam}`}
          method="post"
          onSubmit={handleSocialLogin}
        >
          <Button
            expand
            size="large"
            icon={<GoogleIcon />}
            type="primary"
            variant="only-icon"
            customClass="cgt-button--google-plus justify-content-center"
          />
          {token && <input type="hidden" name="authenticity_token" value={token} />}
        </form>
        <form
          className="email-step__social-form"
          action={`/users/auth/facebook?origin=${originParam}`}
          method="post"
          onSubmit={handleSocialLogin}
        >
          <Button
            expand
            size="large"
            icon={<FacebookIcon />}
            type="primary"
            variant="only-icon"
            customClass="cgt-button--facebook justify-content-center"
          />
          {token && <input type="hidden" name="authenticity_token" value={token} />}
        </form>
      </div>

      <div className="email-step__divider">
        <span className="cgt-text-medium cgt-legacy-color-grey-50">
          {t('subscription_checkout.email_step.divider')}
        </span>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('subscription_checkout.email_step.email_required'),
            pattern: {
              value: EMAIL_PATTERN,
              message: t('subscription_checkout.email_step.email_invalid'),
            },
          }}
          render={({ field }) => (
            <TextInput
              label={t('subscription_checkout.email_step.email_label')}
              id="guest-email"
              name="email"
              inputType="email"
              placeholder={t('subscription_checkout.email_step.email_placeholder')}
              isWarning={!!errors.email}
              helpText={errors.email?.message}
              value={field.value}
              wrapperClassName="auth-tab__field"
              handleChange={field.onChange}
              onBlur={field.onBlur}
              isPilot
              isRequired
            />
          )}
        />

        <Controller
          name="email_confirmation"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t('login.email_confirmation_label')}
              id="guest-email-confirmation"
              name="email_confirmation"
              value={field.value}
              wrapperClassName="auth-tab__field hidden"
              handleChange={field.onChange}
              autoComplete="off"
              isPilot
            />
          )}
        />

        {captchaEnabled && <MTCaptcha captchaId={CAPTCHA_ID} />}

        <p className="email-step__helper-text">
          {t('subscription_checkout.email_step.helper_text')}
        </p>

        <Button
          expand
          size="large"
          actionType="submit"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting
            ? <Spinner size="xsmall" classNames="cgt-spinner--inline" />
            : t('subscription_checkout.email_step.continue_button')}
        </Button>
      </form>

      <p className="email-step__login-link">
        {t('subscription_checkout.email_step.login_link_prefix')}{' '}
        <button
          type="button"
          className="email-step__login-link-button"
          onClick={onLoginClick}
        >
          {t('subscription_checkout.email_step.login_link')}
        </button>
      </p>
    </div>
  )
}

EmailStep.propTypes = {
  locale: string,
  guestCheckoutPath: string,
  onSubmit: func,
  onLoginClick: func,
}

export default EmailStep
